/**
 * Presentation rules for PKB document modal screenshot cards.
 * UI-only: does not affect ingestion, Figma sync, or image fetching.
 */

export const PKB_SCREENSHOT_COPY = {
  screenshotsSectionTitle: 'Screenshots',
  extractedDesignTextSectionTitle: 'Extracted design text',
  extractedDesignTextCardLabel: 'Extracted design text',
};

export function entryHasDesignCards({ hasFigma, figmaFrames } = {}) {
  if (hasFigma) return true;
  return Array.isArray(figmaFrames) && figmaFrames.length > 0;
}

function normalizeOcrText(image) {
  if (!image || typeof image.ocr_text !== 'string') return '';
  return image.ocr_text.trim();
}

/**
 * Resolve how a single PKB image should render in the document modal.
 * Returns null when the card should be omitted entirely.
 */
export function resolveScreenshotPresentation(image, imageBlobUrls, hasDesignCards) {
  if (!image?.id) return null;

  const blobUrl = imageBlobUrls?.[image.id] || '';
  const ocrText = normalizeOcrText(image);
  const alt = image.filename || `Screenshot ${image.id}`;

  if (blobUrl) {
    return {
      key: String(image.id),
      mode: 'image',
      blobUrl,
      alt,
      extractedText: ocrText || null,
    };
  }

  if (hasDesignCards) {
    return null;
  }

  if (ocrText) {
    return {
      key: String(image.id),
      mode: 'extracted_text',
      text: ocrText,
    };
  }

  if (image.file_url) {
    return null;
  }

  return null;
}

export function buildScreenshotSectionModel(images, imageBlobUrls, docContent) {
  const list = Array.isArray(images) ? images : [];
  const hasDesignCards = entryHasDesignCards(docContent);

  const items = list
    .map((image) => resolveScreenshotPresentation(image, imageBlobUrls, hasDesignCards))
    .filter(Boolean);

  if (items.length === 0) {
    return { visible: false, title: null, items: [] };
  }

  const hasImageCards = items.some((item) => item.mode === 'image');
  const title = hasImageCards
    ? PKB_SCREENSHOT_COPY.screenshotsSectionTitle
    : PKB_SCREENSHOT_COPY.extractedDesignTextSectionTitle;

  return { visible: true, title, items };
}

export function truncateExtractedText(text, maxLength = 120) {
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}…`;
}
